import { watch } from '@vue/composition-api'
import { useSettingStore } from '@ms/stores/setting'
import { storeToRefs } from 'pinia'
import { themeModeValueToNameMap, themeTypeValueToNameMap, isThemeModeExists, isThemeTypeExists, isCurrentPageEnabledTheme, getBrowserThemeMode } from '../utils/theme'
import { THEME_MODE, THEME_TYPE } from '@ms/config/theme'

/**
 * @typedef {import('@vue/composition-api').Ref<T>} Ref<T>
 * @template T
 */

export const useDomThemeSwitcher = () => {
  const settingStore = useSettingStore()
  const { themeMode, themeType, systemThemeMode } = storeToRefs(settingStore)

  const ThemeModeToMetaInfo = {
    [THEME_MODE.DARK.value]: {
      'theme-color': '#000',
      'apple-mobile-web-app-status-bar-style': 'black'
    },
    [THEME_MODE.LIGHT.value]: {
      'theme-color': '#fff',
      'apple-mobile-web-app-status-bar-style': 'default'
    }
  }

  const applyThemeMeta = (themeModeValue) => {
    document.documentElement.querySelector('meta[name="theme-color"]')?.setAttribute('content', ThemeModeToMetaInfo[themeModeValue]['theme-color'])
    document.documentElement.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')?.setAttribute('content', ThemeModeToMetaInfo[themeModeValue]['apple-mobile-web-app-status-bar-style'])
  }

  /**
   * Sync theme based on themeMode and themeType in store
   * @param {HTMLElement?} element
   */
  const syncTheme = (element) => {
    const elementToApply = element || document.documentElement

    if (themeMode.value === THEME_MODE.SYSTEM.value && isThemeModeExists(systemThemeMode.value)) {
      elementToApply.setAttribute('data-mode', themeModeValueToNameMap.value.get(systemThemeMode.value))
    } else {
      elementToApply.setAttribute('data-mode', themeModeValueToNameMap.value.get(themeMode.value))
    }

    elementToApply.setAttribute('data-theme', themeTypeValueToNameMap.value.get(themeType.value))

    if (themeMode.value === THEME_MODE.DARK.value) {
      applyThemeMeta(THEME_MODE.DARK.value)
    } else {
      applyThemeMeta(THEME_MODE.LIGHT.value)
    }
  }

  /**
   * Switch to specific theme with themeMode and themeType
   */
  const switchTheme = (options = {}) => {
    const { mode, type } = options

    if (mode != null) {
      if (mode === THEME_MODE.SYSTEM.value) {
        document.documentElement.setAttribute('data-mode', themeModeValueToNameMap.value.get(systemThemeMode.value))
        return
      }

      document.documentElement.setAttribute('data-mode', themeModeValueToNameMap.value.get(mode))
    }

    if (type != null) {
      document.documentElement.setAttribute('data-theme', themeTypeValueToNameMap.value.get(type))
    }
  }

  /**
   * Auto sync theme based on themeMode and themeType in store
   */
  const autoSyncTheme = () => {
    watch(
      [themeMode, systemThemeMode],
      ([currThemeMode, currSystemThemeMode]) => {
        if (currThemeMode === THEME_MODE.SYSTEM.value && isThemeModeExists(currSystemThemeMode)) {
          document.documentElement.setAttribute('data-mode', themeModeValueToNameMap.value.get(currSystemThemeMode))
          return
        }

        if (isThemeModeExists(currThemeMode)) {
          document.documentElement.setAttribute('data-mode', themeModeValueToNameMap.value.get(currThemeMode))
        }
      },
      { immediate: true }
    )

    watch(
      themeType,
      (currThemeType) => {
        if (isThemeTypeExists(currThemeType)) {
          document.documentElement.setAttribute('data-theme', themeTypeValueToNameMap.value.get(currThemeType))
        }
      },
      { immediate: true }
    )
  }

  /**
   * Switch theme to default theme
   */
  const switchDefaultTheme = () => {
    document.documentElement.setAttribute('data-mode', themeModeValueToNameMap.value.get(THEME_MODE.DARK.value))
    document.documentElement.setAttribute('data-theme', themeTypeValueToNameMap.value.get(THEME_TYPE.DEFAULT.value))

    applyThemeMeta(THEME_MODE.DARK.value)
  }

  /**
   * Switch to specific theme with themeMode and themeType for page that has no route
   *
   * If the page is linked to a route. Please use `syncTheme()`
   */
  const syncStandalonePageTheme = () => {
    isCurrentPageEnabledTheme.value = true

    if (themeMode.value === THEME_MODE.SYSTEM.value && isThemeModeExists(systemThemeMode.value)) {
      settingStore.setSystemThemeMode(getBrowserThemeMode())
    }
    settingStore.syncThemeTypeWithConfig()
    syncTheme()
  }

  return {
    syncTheme,
    switchTheme,
    autoSyncTheme,
    switchDefaultTheme,
    syncStandalonePageTheme
  }
}
